import { languagesApi } from "@/api/languages";
import { LanguageType } from "@/types/LanguageTypes";
import { useEffect, useState } from "react";
import { useInformation } from "@/context/InformationContext";
import { NOTIFICATION_TYPES } from "@/constants/NotificationConstants";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { hasAllPermissions } from "@/utils/roleChecks";

// Custom hook to get languages for the topbar switcher and similar UI.
// Automatically detects if user is logged in and fetches user-specific languages (GET /languages/me).
// Otherwise fetches all available languages (for admin pages). Super admins always see all languages (GET /languages).
export const useLanguages = () => {
  const { t } = useTranslation("common");
  const { user } = useAuth();
  const [languages, setLanguages] = useState<LanguageType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { setNotification } = useInformation();

  const isSuperAdmin = hasAllPermissions(user?.sf_role);

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const response = isSuperAdmin
          ? await languagesApi.getAllLanguages()
          : await languagesApi.getLanguages();
        setLanguages(response.data ?? []);
      } catch (error) {
        console.error("Error fetching languages:", error);
        setNotification({
          type: NOTIFICATION_TYPES.ERROR,
          message:
            error instanceof Error
              ? error.message
              : t("languageSwitcher.error_fetching_languages"),
          isVisible: true,
          onClose: () => {},
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (isLoading) {
      fetchLanguages();
    }
  }, [setNotification, t, isLoading, user, isSuperAdmin]);

  return { availableLanguages: languages, isLoading };
};

// All system languages (GET /languages). For client assignment, create client form, etc.
export const useAllSystemLanguages = (options?: { enabled?: boolean }) => {
  const { enabled = true } = options ?? {};
  const { t } = useTranslation("common");
  const [languages, setLanguages] = useState<LanguageType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { setNotification } = useInformation();

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }
    const fetch = async () => {
      try {
        const response = await languagesApi.getAllLanguages();
        setLanguages(response.data ?? []);
      } catch (error) {
        console.error("Error fetching all languages:", error);
        setNotification({
          type: NOTIFICATION_TYPES.ERROR,
          message:
            error instanceof Error
              ? error.message
              : t("languageSwitcher.error_fetching_languages"),
          isVisible: true,
          onClose: () => {},
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetch();
  }, [setNotification, t, enabled]);

  return { allLanguages: languages, isLoading: enabled ? isLoading : false };
};

// Options for useAllLanguages
export interface UseAllLanguagesOptions {
  /** For user edit: languages for this user (client + all their groups). */
  userId?: number | null;
  /** For client/edit/group: languages for this client. */
  clientId?: number | null;
  /** Skip fetch when false (e.g. user not loaded yet). */
  enabled?: boolean;
  /** When this changes, refetches (e.g. after assigned languages are updated). */
  refetchTrigger?: unknown;
}

// Custom hook to get ALL languages (for assignment purposes)
// Use userId for user edit; use clientId for client edit, group edit.
export const useAllLanguages = (options: UseAllLanguagesOptions = {}) => {
  const { userId, clientId, enabled = true, refetchTrigger } = options;

  const { t } = useTranslation("common");
  const [languages, setLanguages] = useState<LanguageType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { setNotification } = useInformation();

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    const id = userId ?? clientId;
    if (id == null || id === 0) {
      setLanguages([]);
      setIsLoading(false);
      return;
    }

    const fetchAllLanguages = async () => {
      try {
        const response =
          userId != null
            ? await languagesApi.getLanguagesForUser(userId)
            : await languagesApi.getLanguagesForClient(clientId!);

        setLanguages(response.data);
      } catch (error) {
        console.error("Error fetching all languages:", error);
        setNotification({
          type: NOTIFICATION_TYPES.ERROR,
          message:
            error instanceof Error
              ? error.message
              : t("languageSwitcher.error_fetching_languages"),
          isVisible: true,
          onClose: () => {},
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllLanguages();
  }, [setNotification, t, userId, clientId, enabled, refetchTrigger]);

  return { allLanguages: languages, isLoading };
};

// Options for useGroupLanguages
export interface UseGroupLanguagesOptions {
  enabled?: boolean;
  /** Fallback when groups have no languages; backend returns client languages. */
  clientId?: number | null;
}

// Languages available for selected groups (union). Use when creating user with groups selected.
export const useGroupLanguages = (
  groupIds: number[],
  options: UseGroupLanguagesOptions = {},
) => {
  const { enabled = true, clientId } = options;
  const { t } = useTranslation("common");
  const [languages, setLanguages] = useState<LanguageType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { setNotification } = useInformation();
  const groupIdsKey = groupIds?.join(",") ?? "";

  useEffect(() => {
    if (!enabled || !groupIds.length) {
      setLanguages([]);
      setIsLoading(false);
      return;
    }

    const fetch = async () => {
      try {
        const response = await languagesApi.getLanguagesForGroups(
          groupIds,
          clientId ?? undefined,
        );
        setLanguages(response.data);
      } catch (error) {
        console.error("Error fetching group languages:", error);
        setNotification({
          type: NOTIFICATION_TYPES.ERROR,
          message:
            error instanceof Error
              ? error.message
              : t("languageSwitcher.error_fetching_languages"),
          isVisible: true,
          onClose: () => {},
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetch();
    // groupIdsKey stabilizes array reference - groupIds would trigger refetch every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupIdsKey, clientId, enabled, setNotification, t]);

  return { allLanguages: languages, isLoading };
};
